import React, { useMemo, memo } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { SingleValue } from 'react-select';
import {
  functionPrimitiveSelector,
  errorPrimitiveSelector,
  functionPercentileSelector,
} from '../../../../redux/selectors/builder';
import {
  removeErrorAction,
  setBuilderFunctionAction,
  setFunctionPercentileAction,
} from '../../../../redux/slices/oldBuilder';
import { IRowMeta } from '../../../../redux/slices/oldBuilder';
import { defaultMeasureFunction } from '../../../../redux/slices/oldBuilder/defaults';
import { IMeasureFunction } from '../../../../utils/apollo/graphqlSchema';
import BuilderNumberInputField from '../../BuilderFields/BuilderNumberInputField';
import {
  MAX_FUNCTION_PERCENTILE,
  MIN_FUNCTION_PERCENTILE,
} from '../../../../config/constants';
import { ReactSelectMenu } from '../../../Dropdown/StyledReactSelect';
import { colors } from '../../../../config/styles';
import { useAppSelector } from '../../../../redux/hooks';

interface IProps {
  rowMeta: IRowMeta;
  measureFunctions: IMeasureFunction[];
}

const StyledNumberInput = styled(BuilderNumberInputField)`
  width: 60px;
`;

export default memo(({ rowMeta, measureFunctions }: IProps) => {
  const dispatch = useDispatch();

  const fnSelector = useMemo(functionPrimitiveSelector, []);
  const fnPercentileSelector = useMemo(functionPercentileSelector, []);
  const errorSelector = useMemo(errorPrimitiveSelector, []);

  const fn = useAppSelector((state) => fnSelector(state, rowMeta));
  const fnPercentile = useAppSelector((state) =>
    fnPercentileSelector(state, rowMeta),
  );

  const hasError = useAppSelector((state) =>
    errorSelector(state, rowMeta, 'fn'),
  );

  const handleChangeFunction = (newOption: SingleValue<IMeasureFunction>) => {
    if (!newOption) {
      dispatch(
        setBuilderFunctionAction({
          rowMeta,
          newFunction: defaultMeasureFunction(),
        }),
      );

      return;
    }

    if (hasError) {
      dispatch(removeErrorAction({ rowMeta, errorKey: 'fn' }));
    }

    dispatch(setBuilderFunctionAction({ rowMeta, newFunction: newOption }));
  };

  const onChangePercentile = (newValue: number | null) => {
    dispatch(setFunctionPercentileAction({ rowMeta, newValue }));
  };

  return (
    <>
      <ReactSelectMenu
        placeholder="Select function to measure"
        isMulti={false}
        value={fn?.key ? fn : null}
        options={measureFunctions}
        onChange={handleChangeFunction}
        getOptionLabel={(option) => option.name}
        getOptionValue={(option) => option.key}
        isSearchable
        isClearable
        hideSelectedOptions
        maxMenuHeight={300}
        styles={{
          container: (base) => ({
            ...base,
            flex: 1,
          }),
          control: (base) => {
            const result = {
              ...base,
              minWidth: '100%',
              maxWidth: '100%',
            };

            if (hasError) {
              return {
                ...result,
                borderColor: colors.mainError,
              };
            }

            return result;
          },
        }}
      />
      {fn?.requirePercentile && (
        <StyledNumberInput
          placeholder="eg. 90"
          value={fnPercentile}
          onChange={onChangePercentile}
          min={MIN_FUNCTION_PERCENTILE}
          max={MAX_FUNCTION_PERCENTILE}
          rowMeta={rowMeta}
          errorKey="percentile"
        />
      )}
    </>
  );
});
